import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function EventChat({ eventId }) {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [denied, setDenied] = useState(false);
  const boxRef = useRef(null);
  const atBottom = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/chat`);
      setMeta(data);
      if (data.available) {
        const m = await api.get(`/events/${eventId}/chat/messages?limit=50`);
        setMsgs(m.data.messages);
        if (data.unread > 0) api.post(`/events/${eventId}/chat/read`).catch(() => {});
      }
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
  }, [eventId]);

  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (atBottom.current && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs]);

  const onScroll = () => { const el = boxRef.current; if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; };

  const send = async () => {
    const v = text.trim();
    if (!v) return;
    setSending(true); atBottom.current = true;
    try { await api.post(`/events/${eventId}/chat/messages`, { content: v }); setText(""); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const del = async (mid) => {
    try { await api.delete(`/events/${eventId}/chat/messages/${mid}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (denied) return null;
  if (!meta) return null;
  if (!meta.available) return (
    <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-5" data-testid="event-chat">
      <div className="flex items-center gap-2 font-head text-lg font-bold"><MessageCircle className="h-5 w-5 text-[#FF4D00]" />Chat do Evento</div>
      <p className="text-sm text-zinc-500 mt-2">O chat será liberado quando houver ao menos um músico confirmado no evento.</p>
    </div>
  );

  return (
    <div className="bg-[#121217] border border-white/[0.06] rounded-sm overflow-hidden" data-testid="event-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 font-head text-lg font-bold"><MessageCircle className="h-5 w-5 text-[#FF4D00]" />Chat do Evento</div>
        <div className="flex -space-x-2">
          {meta.members.slice(0, 6).map((m) => (
            <div key={m.user_id} title={`${m.stage_name}${m.is_organizer ? " (organizador)" : ""}${m.status === "left" ? " — saiu" : ""}`}
              className={`h-7 w-7 rounded-full border-2 border-[#121217] bg-[#1C1C24] overflow-hidden flex items-center justify-center text-[10px] font-bold ${m.status === "left" ? "opacity-40" : ""}`}>
              {m.photo ? <img src={m.photo} alt="" className="h-full w-full object-cover" /> : (m.stage_name?.[0] || "?")}
            </div>
          ))}
        </div>
      </div>

      <div ref={boxRef} onScroll={onScroll} className="h-80 overflow-y-auto px-4 py-3 space-y-2" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-center text-sm text-zinc-600 py-8">Nenhuma mensagem ainda. Diga olá para a equipe!</p>}
        {msgs.map((m) => {
          if (m.message_type === "SYSTEM") return (
            <div key={m.id} className="text-center"><span className="text-[11px] text-zinc-500 bg-[#09090B] border border-white/[0.06] rounded-full px-3 py-1">{m.content}</span></div>
          );
          const mine = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`group max-w-[78%] rounded-sm px-3 py-1.5 ${mine ? "bg-[#FF4D00]/15 border border-[#FF4D00]/25" : "bg-[#1C1C24] border border-white/[0.06]"}`}>
                {!mine && <div className="text-[11px] text-[#00F0FF] font-semibold">{m.sender_name}</div>}
                <div className={`text-sm break-words ${m.deleted ? "italic text-zinc-400" : "text-zinc-100"}`}>{m.content}</div>
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-[10px] text-zinc-400">{timeOf(m.created_at)}</span>
                  {mine && !m.deleted && <button onClick={() => del(m.id)} data-testid={`chat-del-${m.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {meta.can_send ? (
        <div className="flex gap-2 p-3 border-t border-white/[0.06]">
          <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="Escreva uma mensagem..." className="bg-[#09090B] border-white/10 rounded-sm" />
          <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm shrink-0"><Send className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="p-3 border-t border-white/[0.06] text-center text-xs text-zinc-500">Chat em modo somente leitura.</div>
      )}
    </div>
  );
}
